import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { apiCall, successToast, errorToast } from "utils/httpClient";
import apiEndPoints from "utils/apiEndPoints";
import propertyService from "reduxStore/service/propertyService";
import strings from "translate/language";
import "screens/channel_partner/ChannelPartnerListing.css";

const propertyId = (property) =>
  String(property?.property_id || property?._id || "");

const propertyLabel = (property) =>
  property?.property_title || property?.property_name || "Project";

const propertyDate = (property) => {
  const value = property?.createdDate || property?.created_date || 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const extractRows = (res) => {
  const body = res?.data;
  if (Array.isArray(body?.data)) {
    return body.data;
  }
  if (Array.isArray(body)) {
    return body;
  }
  return [];
};

const AllocatePropertyModal = ({
  show,
  cp_id,
  handleClose,
  onPropertiesSelected,
  initialSelected = [],
  persistOnSave = false,
}) => {
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [allPropertyList, setAllPropertyList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }
    setSelectedProperties(Array.isArray(initialSelected) ? initialSelected : []);
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [propertyRes, allocatedRes] = await Promise.all([
          propertyService.getAllProperty({
            offset: 0,
            limit: 500,
            skip_metrics: true,
            suppressGlobalLoader: true,
          }),
          cp_id
            ? apiCall("post", apiEndPoints.GET_CP_PROPERTY_FOR_SM, { cp_id })
            : Promise.resolve(null),
        ]);
        if (cancelled) {
          return;
        }
        const rows = extractRows(propertyRes).sort(
          (a, b) => propertyDate(b) - propertyDate(a)
        );
        setAllPropertyList(rows);
        const allocated = allocatedRes?.data?.data || [];
        if (
          !initialSelected?.length &&
          allocatedRes?.data?.status === 200 &&
          allocated.length
        ) {
          const fromList = rows.filter((row) =>
            allocated.some(
              (item) => propertyId(row) === String(item?.property_id)
            )
          );
          setSelectedProperties(fromList.length ? fromList : allocated);
        }
      } catch (error) {
        if (!cancelled) {
          setAllPropertyList([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [show, cp_id]);

  const propertyOptions = useMemo(
    () =>
      allPropertyList.map((property) => ({
        value: propertyId(property),
        label: propertyLabel(property),
        property,
      })),
    [allPropertyList]
  );

  const selectedOptions = useMemo(
    () =>
      selectedProperties.map((property) => ({
        value: propertyId(property),
        label: propertyLabel(property),
        property,
      })),
    [selectedProperties]
  );

  const handleSaveChanges = async () => {
    if (persistOnSave && cp_id) {
      const propertyTag = selectedProperties.map((item) => ({
        property_id: item.property_id || item._id,
        active_status: true,
        _id: item.property_id || item._id,
        property_title: propertyLabel(item),
        property_type: item?.property_type,
      }));
      const response = await apiCall(
        "post",
        apiEndPoints.UPDATE_CP_PROPERTY_SM,
        {
          cp_id,
          property_tag: JSON.stringify(propertyTag),
        }
      );
      if (response?.data?.status === 200) {
        successToast(response.data.message);
      } else {
        errorToast(
          response?.data?.message || "Something went wrong. Please try again."
        );
        return;
      }
    }
    if (onPropertiesSelected) {
      onPropertiesSelected(selectedProperties);
    }
    handleClose();
  };

  if (!show) {
    return null;
  }

  return (
    <div
      className="justo-allocate-modal__backdrop"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="justo-allocate-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="justo-allocate-modal__head">
          <div>
            <h5 className="justo-allocate-modal__title">
              {strings.allocateProperty}
            </h5>
            <p className="justo-allocate-modal__hint">
              Search and select projects. Newest projects appear first.
            </p>
          </div>
          <button
            type="button"
            className="justo-allocate-modal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="justo-allocate-modal__body">
          <label className="justo-allocate-modal__label">
            {strings.propertyName}
          </label>
          <Select
            classNamePrefix="justo-cp-select"
            isMulti
            isSearchable
            isClearable
            isLoading={loading}
            options={propertyOptions}
            value={selectedOptions}
            placeholder={loading ? "Loading projects…" : "Select property"}
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
            maxMenuHeight={280}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 10060 }),
              valueContainer: (base) => ({
                ...base,
                flexWrap: "wrap",
                maxHeight: 132,
                overflowY: "auto",
              }),
              multiValue: (base) => ({
                ...base,
                background: "#eef2ff",
                border: "1px solid #c7d2fe",
                borderRadius: 999,
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: "#1e3a8a",
                fontWeight: 500,
              }),
            }}
            onChange={(options) =>
              setSelectedProperties(
                (options || []).map((option) => option.property)
              )
            }
          />
          <p className="justo-allocate-modal__empty">
            {selectedProperties.length} project
            {selectedProperties.length === 1 ? "" : "s"} selected
          </p>
        </div>
        <div className="justo-allocate-modal__foot">
          <button
            type="button"
            className="justo-cp__btn justo-cp__btn--ghost"
            onClick={handleClose}
          >
            {strings.cancel}
          </button>
          <button
            type="button"
            className="justo-cp__btn justo-cp__btn--solid"
            onClick={handleSaveChanges}
          >
            <i className="fa fa-check" aria-hidden="true" />
            {strings.submit}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllocatePropertyModal;
